import {
  EProductCardTagType,
  IProductCardInfoLabel,
  IProductCardInfoLine,
  type IProductCardTag,
  type ISimplifiedProduct,
} from '@pu/mpp-ui/components/product-card';
import { IPromoCart } from 'core/model/interfaces/discount.interface';
import {
  ICartProduct,
  ICartUnitMeasure,
  IDetailedPromoIndicators,
  IProduct,
  IPromoIndicators,
  IUnitMeasure,
  IUnitMeasureDetail,
} from 'core/model/interfaces/product.interface';
import { EXCLUSIVE_PROMO_LABEL } from 'shared/components/promotions/exclusive-promo-label';
import { cloneDeepSimple } from './object.utils';

interface ICardProductData {
  product: ISimplifiedProduct;
  unitMeasureCode: string;
  tags: Array<IProductCardTag>;
  infoLine: IProductCardInfoLine | undefined;
  infoLabel: IProductCardInfoLabel | undefined;
}

interface ISearchCardProductData {
  product: ISimplifiedProduct;
  tags: Array<IProductCardTag>;
  infoLine: IProductCardInfoLine | undefined;
  infoLabel: IProductCardInfoLabel | undefined;
}

interface IProductToCardProductDataParams {
  product: IProduct;
  cartProduct?: ICartProduct;
}

export const updateSelectedUnitMeasure = <T extends { code: string }>(
  unitMeasure: T,
  selectedUnitMeasureCode: string,
  newUnitMeasure?: T,
) =>
  unitMeasure.code === selectedUnitMeasureCode
    ? { ...(newUnitMeasure ?? unitMeasure), selected: true }
    : { ...unitMeasure, selected: false };

export const getStockBySelectedUnitMeasureDetail = (selectedUnitMeasure?: IUnitMeasureDetail): number =>
  selectedUnitMeasure?.stock || 0;

export const getPresentationIcon = (unitMeasure: ICartUnitMeasure) => (unitMeasure.default ? 'box-milk' : 'package');

export const shouldHighlightFinalPrice = (unitMeasure: IUnitMeasure | IUnitMeasureDetail | ICartUnitMeasure) =>
  unitMeasure.promoIndicators && 'hasDiscount' in unitMeasure.promoIndicators
    ? unitMeasure.promoIndicators.hasDiscount
    : unitMeasure.promoIndicators.hasAnyDiscount;

export const getTags = (promoIndicators: IPromoIndicators | IDetailedPromoIndicators): Array<IProductCardTag> => {
  const tags: Array<IProductCardTag> = [];

  if (promoIndicators.hasAnyDiscount || promoIndicators.hasAnyBonus)
    tags.push({ labelText: 'Promoción', type: EProductCardTagType.POSITIVE });
  if (promoIndicators.hasAnyCombo) tags.push({ labelText: 'Promo Combo', type: EProductCardTagType.NEGATIVE });

  return tags;
};

export const getInfoLine = (
  promoIndicators: IPromoIndicators | IDetailedPromoIndicators,
): IProductCardInfoLine | undefined => {
  if (promoIndicators.maxDiscount)
    return {
      text: `Hasta ${promoIndicators.maxDiscount} de dscto`,
      icon: { name: 'promotion', weight: 'fill' },
    };

  if (promoIndicators.hasAnyBonus) return { text: '¡Llévate un regalo!', icon: { name: 'gift', weight: 'fill' } };
};

export const getLabel = (
  promoIndicators: IPromoIndicators | IDetailedPromoIndicators,
): IProductCardInfoLabel | undefined => {
  if (promoIndicators.isExclusive) {
    return EXCLUSIVE_PROMO_LABEL;
  }
};
export const getProductTotalDiscountValue = (discounts: Array<IPromoCart> = []) =>
  discounts.reduce((current, promo) => {
    const value = promo.value || 0;
    return current + value;
  }, 0);

export const mapProductToCardProductData = ({
  product,
  cartProduct,
}: IProductToCardProductDataParams): ICardProductData => {
  const finalProduct = cloneDeepSimple(product);

  if (cartProduct) {
    finalProduct.unitMeasures = finalProduct.unitMeasures.map(um =>
      updateSelectedUnitMeasure(um, cartProduct.unitMeasure.code),
    );
  }
  const finalProductUnitMeasure = finalProduct.unitMeasures[0];
  const { promoIndicators } = finalProductUnitMeasure;

  const selectedUnitMeasure = cartProduct
    ? {
        ...cartProduct.unitMeasure,
        price: finalProductUnitMeasure.price,
        presentation: finalProductUnitMeasure.presentation,
      }
    : finalProductUnitMeasure;
  const { originalPrice, presentation, code: unitMeasureCode, price } = selectedUnitMeasure;
  const { currency, image, stock, name } = finalProduct;

  return {
    product: {
      active: true,
      currencySymbol: currency?.symbol,
      finalPrice: price,
      highlightFinalPrice: shouldHighlightFinalPrice(finalProductUnitMeasure),
      image,
      regularPrice: originalPrice,
      stock,
      subtitle: presentation,
      title: name,
    },
    unitMeasureCode,
    tags: getTags(promoIndicators),
    infoLabel: getLabel(promoIndicators),
    infoLine: getInfoLine(promoIndicators),
  };
};

export const productToSearchProductPreviewProps = (product: IProduct): ISearchCardProductData => {
  const selectedUnitMeasure = product.unitMeasures[0];
  const { promoIndicators } = selectedUnitMeasure;
  const { originalPrice, presentation, price } = selectedUnitMeasure;
  return {
    product: {
      title: product.name,
      regularPrice: originalPrice,
      highlightFinalPrice: shouldHighlightFinalPrice(selectedUnitMeasure),
      image: product.image,
      subtitle: presentation,
      finalPrice: price,
      currencySymbol: product.currency.symbol,
      active: true,
      stock: product.stock,
    },
    tags: getTags(promoIndicators),
    infoLine: getInfoLine(promoIndicators),
    infoLabel: getLabel(promoIndicators),
  };
};

export const hasAllKindsOfPromos = <T extends { promoIndicators: IDetailedPromoIndicators }>(unitMeasure: T) =>
  unitMeasure.promoIndicators.hasAnyBonus ||
  unitMeasure.promoIndicators.hasAnyDiscount ||
  unitMeasure.promoIndicators.hasAnyCombo;

export const getUnitMeasureByDefault = <T extends { default: boolean }>(unitMeasures: Array<T>, isDefault = true) =>
  unitMeasures.find(um => um.default === isDefault) ?? unitMeasures[0];

export const getDefaultUnitMeasure = <T extends { default: boolean }>(unitMeasures: Array<T>) =>
  unitMeasures.find(um => um.default === true) ?? unitMeasures[0];

export const getSelectedUnitMeasure = <T extends { default: boolean; selected?: boolean }>(unitMeasures: Array<T>) =>
  unitMeasures.find(unitMeasure => unitMeasure.selected) ?? unitMeasures[0];

export const cartProductToProduct = (product: ICartProduct): IProduct => ({
  ...product,
  unitMeasures: [
    {
      ...product.unitMeasure,
      promoIndicators: {
        ...product.unitMeasure.promoIndicators,
        hasAnyDiscount: product.unitMeasure.promoIndicators.hasDiscount,
        hasAnyCombo: false,
        hasAnyBonus: false,
        isExclusive: false,
        hasSimpleDiscount: false,
        hasTieredDiscount: false,
        hasSimpleBonus: false,
        hasTieredBonus: false,
        hasComboDiscount: false,
        hasComboBonus: false,
      },
    },
  ],
});

export const getUnitMeasureByCode = <T extends { code: string }>(unitMeasures: Array<T>, code: string) =>
  unitMeasures.find(unitMeasure => unitMeasure.code === code) ?? unitMeasures[0];

export const getDiscountPercentage = (discount = 0) => {
  const percentage = discount * 100;
  const hasDecimals = String(percentage).includes('.');
  const formattedPercentage = hasDecimals ? Number(percentage.toFixed(2)) : percentage;
  return formattedPercentage;
};

export const getPresentationTextForDetail = <
  T extends {
    conversionFactor: string | null;
    isBase: boolean;
    name: string;
    unitMeasure: string;
  },
>(
  presentation: T,
  length = 1,
): string => {
  if (!presentation.conversionFactor) return '';
  if (presentation.isBase && length > 1) return `${presentation.name} de ${presentation.unitMeasure}`;
  return presentation.conversionFactor;
};
